import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Users, Phone, MapPin, Briefcase, AlertCircle, Bell, Loader2, Megaphone, Link2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

function AddClientDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", city: "", occupation: "", employer: "", annual_income: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Name and mobile are required");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form };
      if (body.annual_income === "") delete body.annual_income;
      else body.annual_income = Number(body.annual_income);
      const r = await api.post("/clients", body);
      toast.success("Client added");
      onCreated(r.data);
      setForm({ name: "", mobile: "", email: "", city: "", occupation: "", employer: "", annual_income: "", notes: "" });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="add-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add a new client</DialogTitle>
          <DialogDescription>Track this client&apos;s loans, insurance, investments, and family in one place.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="sm:col-span-2">
            <Label>Full name *</Label>
            <Input data-testid="client-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ravi Kumar" />
          </div>
          <div>
            <Label>Mobile *</Label>
            <Input data-testid="client-mobile-input" value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="9876543210" />
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="client-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="ravi@example.com" />
          </div>
          <div>
            <Label>City</Label>
            <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Mumbai" />
          </div>
          <div>
            <Label>Occupation</Label>
            <Input value={form.occupation} onChange={(e) => set("occupation", e.target.value)} placeholder="Software Engineer" />
          </div>
          <div>
            <Label>Employer</Label>
            <Input value={form.employer} onChange={(e) => set("employer", e.target.value)} placeholder="ABC Corp" />
          </div>
          <div>
            <Label>Annual income (₹)</Label>
            <Input type="number" value={form.annual_income} onChange={(e) => set("annual_income", e.target.value)} placeholder="1200000" />
          </div>
          <div className="sm:col-span-2">
            <Label>Notes</Label>
            <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Optional notes about this client" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="cancel-add-client">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-900 hover:bg-emerald-800" data-testid="save-client-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [oneMap, setOneMap] = useState({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [c360, setC360] = useState(null);
  useEffect(() => { api.get("/customer360/summary").then((r) => setC360(r.data)).catch(() => {}); }, []);
  const [broadcastText, setBroadcastText] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);

  const sendBroadcast = async () => {
    setBroadcasting(true);
    try {
      const r = await api.post("/agent/one-clients/broadcast", { text: broadcastText.trim() });
      toast.success(`Message sent to ${r.data.sent} app client${r.data.sent === 1 ? "" : "s"}`);
      setShowBroadcast(false);
      setBroadcastText("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Broadcast failed"); }
    setBroadcasting(false);
  };

  const load = React.useCallback(async (searchQuery) => {
    setLoading(true);
    try {
      const [list, rem, om] = await Promise.all([
        api.get(`/clients${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ""}`),
        api.get("/clients/reminders"),
        api.get("/agent/one-clients/map").catch(() => ({ data: {} })),
      ]);
      setClients(list.data);
      setReminders(rem.data.items || []);
      setOneMap(om.data || {});
    } catch (e) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { load(q); }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, load]);

  return (
    <div className="space-y-8" data-testid="clients-page">
      <Dialog open={showBroadcast} onOpenChange={setShowBroadcast}>
        <DialogContent data-testid="broadcast-dialog">
          <DialogHeader>
            <DialogTitle>Message all app clients</DialogTitle>
            <DialogDescription>Sends one chat message to every Crece One client connected to you ({Object.keys(oneMap).length}).</DialogDescription>
          </DialogHeader>
          <textarea data-testid="broadcast-text" value={broadcastText} onChange={(e) => setBroadcastText(e.target.value)}
            placeholder="e.g. Weekly tip: review your emergency fund — aim for 6 months of expenses."
            className="w-full min-h-[110px] rounded-lg border border-stone-300 p-3 text-sm" maxLength={2000} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBroadcast(false)}>Cancel</Button>
            <Button data-testid="broadcast-send" disabled={broadcasting || broadcastText.trim().length < 2} onClick={sendBroadcast}
              className="bg-emerald-900 hover:bg-emerald-800">{broadcasting ? "Sending…" : "Send to all"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Client Portfolios</h1>
          <p className="text-stone-600 mt-2 max-w-xl">Manage every client&apos;s complete financial picture — loans, insurance, investments, family — in one place.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/app/renewals">
            <Button variant="outline" className="gap-2 border-stone-300" data-testid="open-renewals"><CalendarClock className="w-4 h-4" /> Renewals{c360?.renewals_month ? ` (${c360.renewals_month})` : ""}</Button>
          </Link>
          {Object.keys(oneMap).length > 0 && (
            <Button variant="outline" onClick={() => setShowBroadcast(true)} className="gap-2 border-emerald-900 text-emerald-900" data-testid="open-broadcast">
              <Megaphone className="w-4 h-4" /> Message app clients
            </Button>
          )}
          <Button onClick={() => setShowAdd(true)} className="bg-emerald-900 hover:bg-emerald-800 gap-2" data-testid="open-add-client">
            <Plus className="w-4 h-4" /> Add Client
          </Button>
        </div>
      </div>

      {c360?.unlinked > 0 && (
        <Link to="/app/clients/cleanup" className="block bento-dark rounded-2xl px-5 py-4" data-testid="cleanup-banner">
          <div className="relative z-10 flex items-center gap-3 flex-wrap"><Link2 className="w-5 h-5 text-saffron-300" /><div className="text-sm"><b>{c360.unlinked} unlinked decodes &amp; vault policies</b> — confirm which client they belong to so renewals and 360 cards are complete.</div><span className="ml-auto rounded-full bg-saffron-500 text-emerald-950 font-black text-xs px-4 py-2">Review →</span></div>
        </Link>
      )}

      {reminders.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="reminders-panel">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-5 h-5 text-amber-700" />
            <h2 className="font-semibold text-amber-900">Due This Week ({reminders.length})</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {reminders.slice(0, 8).map((r) => (
              <Link key={`${r.kind}-${r.ref_id}`} to={`/app/clients/${r.client_id}`}
                className="bg-white rounded-xl border border-amber-200 p-3 flex items-center justify-between hover:border-amber-400 transition"
                data-testid={`reminder-${r.ref_id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{r.title}</div>
                  <div className="text-xs text-stone-500 truncate">{r.client_name} · {r.subtitle}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-xs font-bold ${r.overdue ? "text-red-600" : "text-amber-700"}`}>
                    {r.overdue ? "OVERDUE" : "Due"}
                  </div>
                  <div className="text-xs text-stone-500">{r.due_date}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-stone-200 p-5">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, mobile, city..." className="pl-10" />
        </div>

        <div className="mt-5">
          {loading ? (
            <div className="text-center py-12 text-stone-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : clients.length === 0 ? (
            <div className="text-center py-16 text-stone-500" data-testid="clients-empty">
              <Users className="w-12 h-12 mx-auto mb-3 text-stone-300" />
              <p className="font-semibold">No clients yet</p>
              <p className="text-sm mt-1">Add your first client to start building their portfolio.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map((c) => (
                <Link key={c.id} to={`/app/clients/${c.id}`} data-testid={`client-card-${c.id}`}
                  className="border border-stone-200 rounded-xl p-4 hover:border-emerald-700 hover:shadow-md transition bg-white">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-lg truncate">{c.name}</div>
                      <div className="text-xs text-stone-500 flex items-center gap-1 mt-1">
                        <Phone className="w-3 h-3" /> {c.mobile}
                      </div>
                      {c.city && (
                        <div className="text-xs text-stone-500 flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3" /> {c.city}
                        </div>
                      )}
                      {c.occupation && (
                        <div className="text-xs text-stone-500 flex items-center gap-1 mt-0.5">
                          <Briefcase className="w-3 h-3" /> {c.occupation}
                        </div>
                      )}
                    </div>
                    {(() => {
                      const one = oneMap[c.id];
                      const src = c.source === "crece_one" ? { t: "CRECE ONE", cls: "bg-amber-100 text-amber-800" }
                        : c.source === "lead" ? { t: "FROM LEAD", cls: "bg-emerald-100 text-emerald-800" }
                        : c.source === "offline_quote" ? { t: "FROM QUOTE", cls: "bg-blue-100 text-blue-800" }
                        : { t: "DIRECT", cls: "bg-stone-100 text-stone-600" };
                      return (
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className={`text-[10px] px-2 py-1 rounded-full font-bold whitespace-nowrap ${src.cls}`} data-testid={`client-source-${c.id}`}>{src.t}</span>
                          {one && (
                            <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-900 text-amber-300 font-bold whitespace-nowrap flex items-center gap-1" data-testid={`client-app-badge-${c.id}`}>
                              APP{one.overall_score !== null && one.overall_score !== undefined ? ` · ${one.overall_score}/100` : ""}
                              {one.unread > 0 && <span className="min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-emerald-950 grid place-items-center">{one.unread}</span>}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="grid grid-cols-5 gap-1 mt-4 pt-3 border-t border-stone-100 text-center">
                    <div><div className="font-bold text-sm">{c._counts?.loans || 0}</div><div className="text-[10px] text-stone-500 uppercase">Loans</div></div>
                    <div><div className="font-bold text-sm">{c._counts?.insurance || 0}</div><div className="text-[10px] text-stone-500 uppercase">Ins</div></div>
                    <div><div className="font-bold text-sm">{c._counts?.investments || 0}</div><div className="text-[10px] text-stone-500 uppercase">Inv</div></div>
                    <div><div className="font-bold text-sm">{c._counts?.family || 0}</div><div className="text-[10px] text-stone-500 uppercase">Fam</div></div>
                    <div><div className="font-bold text-sm">{c._counts?.crece || 0}</div><div className="text-[10px] text-stone-500 uppercase">Crece</div></div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <AddClientDialog open={showAdd} onClose={() => setShowAdd(false)} onCreated={(c) => { setClients((p) => [c, ...p]); }} />
    </div>
  );
}
